import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  GridState,
  SelectionChangedEvent,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { observer } from 'mobx-react-lite';

import { useDataGridApiSetter } from './data-grid.provider';
import {
  checkConstraintsUpdate,
  extractConstraints,
} from './handle-constraints';
import { AG_GRID_LOCALE_RU } from './ru.local';
import { InitialConstraints } from './types';

import 'ag-grid-community/styles/ag-grid.css'; // Core CSS
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Theme
import './theme.less'; // Theme override

import {
  checkColumnsSettingsUpdate,
  extractColumnsSettins,
} from './columns-settings/handle-cols-settings';
import {
  readPersistColumnsSettings,
  savePersistColumnsSettings,
} from './columns-settings/persist-columns-settings';

type LocalCompProps = {
  initialConstraints?: InitialConstraints;
  hasMultiSort?: boolean;
  persistSettingsId?: string;
  onGridReady?: (event: GridReadyEvent) => void;
  onChange?: (constraints: InitialConstraints) => void;
  onSelect?: (selected: { id: number }[]) => void;
};

export type DataGridProps = AgGridReactProps & LocalCompProps;

const defaultProps: DataGridProps = {
  hasMultiSort: true,
};

export const DataGrid = observer((props: DataGridProps) => {
  const {
    initialConstraints,
    persistSettingsId,
    initialState,
    hasMultiSort,
    gridOptions,
    onGridReady,
    onChange,
    onSelect,
  } = props;
  const gridApiRef = useRef<AgGridReact<any>>(null);
  const gridApiProviderSetter = useDataGridApiSetter();

  const initialConstraintsData = useMemo<GridState>(() => {
    const _pagination = initialConstraints?.pagination ?? {};
    const _sort = initialConstraints?.sort ?? [];
    const _filters = initialConstraints?.filter ?? {};

    return {
      pagination: _pagination,
      sort: { sortModel: _sort },
      filter: { filterModel: _filters },
    };
  }, [
    initialConstraints?.filter,
    initialConstraints?.pagination,
    initialConstraints?.sort,
  ]);

  const [intitialColumnsSettings] = useState(() => {
    if (!persistSettingsId) return {};

    return readPersistColumnsSettings({
      persistSettingsId,
    });
  });

  const handleStateChanged = useCallback(
    (event: StateUpdatedEvent) => {
      if (persistSettingsId) {
        const hasReasonToUpdateColumnsSettings =
          checkColumnsSettingsUpdate(event);

        if (hasReasonToUpdateColumnsSettings) {
          const columnsSettings = extractColumnsSettins(event);
          savePersistColumnsSettings({
            persistSettingsId,
            state: columnsSettings,
            onFail: (e) =>
              alert(
                `Ошибка при сохранении настроек таблицы ${persistSettingsId}: ${e.message}`,
              ),
          });
        }
      }

      if (!onChange) return;

      // constraints
      const hasReasonToUpdateConstraints = checkConstraintsUpdate(event);
      if (hasReasonToUpdateConstraints) {
        const constraints = extractConstraints(event);
        onChange(constraints);
      }
    },
    [onChange, persistSettingsId],
  );

  const handleSelect = useCallback(
    (event: SelectionChangedEvent) => {
      if (event.source.includes('gridInitializing')) return;
      if (!onSelect) return;

      const selected = event.api.getSelectedRows() as { id: number }[];
      onSelect(selected);
    },
    [onSelect],
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      // hack for activate filters, when row data is empty
      // otherwise initialFilters is inactive
      event.api.updateGridOptions({ rowData: [{ id: 0 }] });
      event.api.updateGridOptions({ rowData: props.rowData });

      gridApiProviderSetter(event.api);

      onGridReady && onGridReady(event);
    },
    [gridApiProviderSetter, onGridReady, props.rowData],
  );

  const gridOptionsData = useMemo<GridOptions>(
    () => ({
      debug: false,
      initialState: {
        ...initialState,
        ...initialConstraintsData,
        ...intitialColumnsSettings,
      },
      suppressColumnVirtualisation: true,
      suppressMenuHide: true,
      suppressRowClickSelection: true,
      suppressCellFocus: true,
      reactiveCustomComponents: true,
      alwaysMultiSort: hasMultiSort || defaultProps.hasMultiSort,
      paginationPageSize: 50,
      paginationPageSizeSelector: [50, 100, 200],
      localeText: AG_GRID_LOCALE_RU,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      rowBuffer: 20,
      onStateUpdated: handleStateChanged,
      onSelectionChanged: handleSelect,
      onGridReady: handleGridReady,
      ...gridOptions,
    }),
    [
      initialState,
      initialConstraintsData,
      intitialColumnsSettings,
      hasMultiSort,
      handleStateChanged,
      handleSelect,
      handleGridReady,
      gridOptions,
    ],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      ...props.defaultColDef,
    }),
    [props.defaultColDef],
  );

  return (
    <div className="ag-theme-quartz" style={{ height: '100%' }}>
      <AgGridReact
        {...props}
        ref={gridApiRef}
        defaultColDef={defaultColDef}
        gridOptions={gridOptionsData}
      />
    </div>
  );
});
